import type { UseRolesResult } from '@deloitte-us-consulting/gp-sdk';
import type { PluginEntry } from '../remotes/registry';

/**
 * Central gate for whether the current user may see/open a registered app.
 * Two INDEPENDENT checks, both optional (ADR 0010):
 *  - `plugin.requiredRole` (set via the registry admin's "Required role(s)"
 *    field — see AppRegistryAdmin.tsx) is a list of RBAC role ids (e.g.
 *    'zenith'); the user needs at least one of them. Empty/absent means no
 *    role is required (today's default for most apps — see
 *    api/src/rbac/manifests.ts `defaultPermissions`).
 *  - `plugin.requiredAlliance` is a list of alliance ids; the user's
 *    resolved alliance-access set (from `rbac.access.allianceAccess`) must
 *    be "All" or intersect it. Empty/absent means no alliance is required.
 * A plugin can require either, both, or neither. Neither = open to every
 * authenticated user. This is client-side UX gating only — exactly as
 * strong (and no stronger) than `requiredRole` already was; it is not a new
 * security boundary.
 *
 * The platform `admin` role (api/src/rbac/platform-roles.ts ADMIN_ROLE_ID)
 * always passes, same as the `gp-admin` break-glass bypass — an app should
 * never be invisible to admins just because its registry entry forgot to
 * list 'admin' in requiredRole.
 */
export function hasPluginAccess(plugin: PluginEntry, rbac: UseRolesResult): boolean {
  if (rbac.isPlatformAdmin || rbac.hasRole('admin')) return true;

  const requiredRoles = Array.isArray(plugin.requiredRole)
    ? plugin.requiredRole
    : typeof (plugin.requiredRole as unknown) === 'string'
      ? [(plugin.requiredRole as unknown) as string]
      : [];
  if (requiredRoles.length) {
    const hasAnyRequiredRole = requiredRoles.some((r) => rbac.hasRole(r));
    if (!hasAnyRequiredRole) return false;
  }

  const requiredAlliances = Array.isArray(plugin.requiredAlliance)
    ? plugin.requiredAlliance
    : typeof (plugin.requiredAlliance as unknown) === 'string'
      ? [(plugin.requiredAlliance as unknown) as string]
      : [];
  if (requiredAlliances.length) {
    const allianceAccess = rbac.access?.allianceAccess;
    const hasAlliance =
      allianceAccess?.all || (requiredAlliances.some((id) => allianceAccess?.allianceIds.includes(id)) ?? false);
    if (!hasAlliance) return false;
  }

  return true;
}
